import { useEffect, useRef } from 'react';
import { useAccessibility } from '@/hooks/useAccessibility';

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccessibilityPanel({ isOpen, onClose }: AccessibilityPanelProps) {
  const {
    settings,
    increaseFontSize,
    decreaseFontSize,
    toggleLargeCursor,
    resetSettings,
  } = useAccessibility();

  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const fontSizeLabel = {
    normal: 'Normal (100%)',
    large: 'Grande (115%)',
    xlarge: 'Extra Grande (130%)',
  }[settings.fontSize];

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="fixed right-0 top-0 h-full w-72 bg-white border-l border-slate-200 shadow-2xl z-[9999] flex flex-col"
      style={{ transition: 'transform 0.3s ease' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-500 to-red-600">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-eye-line text-white text-lg"></i>
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">Acessibilidade</h2>
            <p className="text-xs text-white/80">Personalize sua experiência</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
          aria-label="Fechar painel de acessibilidade"
        >
          <i className="ri-close-line text-white text-xl"></i>
        </button>
      </div>

      {/* Options */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">

        {/* Tamanho da Fonte */}
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-100 flex-shrink-0">
              <i className="ri-font-size text-xl text-orange-600"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Tamanho da Fonte</h3>
              <p className="text-xs text-slate-500 mt-0.5">{fontSizeLabel}</p>
            </div>
          </div>

          {/* Barra de progresso visual */}
          <div className="flex gap-1.5 mb-3">
            {(['normal', 'large', 'xlarge'] as const).map((size) => (
              <div
                key={size}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  settings.fontSize === size
                    ? 'bg-orange-500'
                    : settings.fontSize === 'xlarge' && size === 'large'
                    ? 'bg-orange-300'
                    : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={decreaseFontSize}
              disabled={settings.fontSize === 'normal'}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm font-medium text-slate-700"
              aria-label="Diminuir fonte"
            >
              <i className="ri-subtract-line"></i>
              Diminuir
            </button>
            <button
              onClick={increaseFontSize}
              disabled={settings.fontSize === 'xlarge'}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm font-medium text-orange-700"
              aria-label="Aumentar fonte"
            >
              <i className="ri-add-line"></i>
              Aumentar
            </button>
          </div>
        </div>

        {/* Cursor Maior */}
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                settings.largeCursor ? 'bg-orange-100' : 'bg-slate-200'
              }`}>
                <i className={`ri-cursor-line text-xl ${settings.largeCursor ? 'text-orange-600' : 'text-slate-600'}`}></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Cursor Maior</h3>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Aumenta o cursor e a área de clique dos elementos
                </p>
              </div>
            </div>
            <button
              onClick={toggleLargeCursor}
              className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
                settings.largeCursor ? 'bg-orange-500' : 'bg-slate-300'
              }`}
              role="switch"
              aria-checked={settings.largeCursor}
              aria-label="Ativar cursor maior"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                  settings.largeCursor ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="p-5 border-t border-slate-200">
        <button
          onClick={resetSettings}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer text-sm font-medium text-slate-600"
        >
          <i className="ri-refresh-line"></i>
          Restaurar padrões
        </button>
        <p className="text-xs text-slate-400 text-center mt-3">
          Preferências salvas automaticamente
        </p>
      </div>
    </div>
  );
}

// Botão flutuante para abrir o painel
interface AccessibilityButtonProps {
  onClick: () => void;
  isOpen: boolean;
}

export function AccessibilityButton({ onClick, isOpen }: AccessibilityButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`fixed right-0 top-1/2 -translate-y-1/2 z-[9998] flex items-center justify-center w-10 h-10 rounded-l-xl shadow-lg transition-all duration-200 cursor-pointer ${
        isOpen
          ? 'bg-slate-700 text-white'
          : 'bg-white border border-slate-200 text-slate-600 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200'
      }`}
      aria-label="Abrir painel de acessibilidade"
      title="Acessibilidade"
    >
      <i className="ri-eye-line text-lg"></i>
    </button>
  );
}
